// ============================================================================
// USE VISIBLE NODES - Hook para Flattened View de Nós com Grupos Expandidos
// ============================================================================
//
// Este hook implementa a estratégia "Flattened View" para suportar grupos
// expandidos no cálculo de conexões (edges).
//
// Quando um grupo está:
// - MINIMIZADO: O SubProcessCard aparece como nó único
// - EXPANDIDO: O SubProcessCard é substituído por seus filhos na lista
//
// Também fornece mapeamento para redirecionar conexões:
// - Conexões que entravam no grupo → vão para o primeiro filho
// - Conexões que saíam do grupo → saem do último filho
//
// ============================================================================

import { useMemo } from 'react';
import { CardData } from '../types';

// ============================================================================
// TIPOS
// ============================================================================

export interface ExpandedGroupInfo {
  /** ID do SubProcessCard expandido */
  groupId: string;
  /** Cards filhos do grupo (ordenados por posição X) */
  childCards: CardData[];
  /** Primeiro card do grupo (entrada) */
  entryCard: CardData;
  /** Último card do grupo (saída) */
  exitCard: CardData;
}

export interface EdgeRedirectMap {
  /** Mapeia groupId -> entryCardId (para conexões de entrada) */
  entryRedirects: Map<string, string>;
  /** Mapeia groupId -> exitCardId (para conexões de saída) */
  exitRedirects: Map<string, string>;
  /** Mapeia childCardId -> groupId (para saber de qual grupo um card faz parte) */
  childToGroup: Map<string, string>;
}

export interface UseVisibleNodesResult {
  /** Lista achatada de nós visíveis (sem grupos expandidos, com seus filhos) */
  visibleNodes: CardData[];
  /** Informações sobre grupos expandidos */
  expandedGroupsInfo: Map<string, ExpandedGroupInfo>;
  /** Mapeamento para redirecionamento de conexões */
  redirectMap: EdgeRedirectMap;
  /** Verifica se um card é filho de um grupo expandido */
  isExpandedChild: (cardId: string) => boolean;
  /** Obtém o ID do card de entrada para um grupo (ou o próprio ID se não for grupo) */
  getEntryNodeId: (cardId: string) => string;
  /** Obtém o ID do card de saída para um grupo (ou o próprio ID se não for grupo) */
  getExitNodeId: (cardId: string) => string;
}

// ============================================================================
// FUNÇÕES AUXILIARES
// ============================================================================

/**
 * Ordena cards por posição X (da esquerda para direita)
 * Em caso de empate, ordena por Y (de cima para baixo)
 */
function sortCardsByPosition(cards: CardData[]): CardData[] {
  return [...cards].sort((a, b) => {
    const dx = a.posicao.x - b.posicao.x;
    if (Math.abs(dx) > 10) return dx;
    return a.posicao.y - b.posicao.y;
  });
}

/**
 * Encontra o card mais à esquerda (entrada do grupo)
 */
function findEntryCard(cards: CardData[]): CardData {
  if (cards.length === 0) throw new Error('Cannot find entry card in empty group');
  return cards.reduce((leftmost, card) => 
    card.posicao.x < leftmost.posicao.x ? card : leftmost
  , cards[0]);
}

/**
 * Encontra o card mais à direita (saída do grupo)
 */
function findExitCard(cards: CardData[]): CardData {
  if (cards.length === 0) throw new Error('Cannot find exit card in empty group');
  return cards.reduce((rightmost, card) => {
    const cardWidth = card.tipo === 'decisao' ? 160 : 240;
    const rightmostWidth = rightmost.tipo === 'decisao' ? 160 : 240;
    const cardRight = card.posicao.x + cardWidth;
    const rightmostRight = rightmost.posicao.x + rightmostWidth;
    return cardRight > rightmostRight ? card : rightmost;
  }, cards[0]);
}

/**
 * Verifica se um card é um SubProcessCard agrupado
 */
function isGroupedSubProcess(card: CardData): boolean {
  return card.tipo === 'sub_processo' && 
         !!card.groupedCardIds && 
         card.groupedCardIds.length > 0;
}

// ============================================================================
// HOOK PRINCIPAL
// ============================================================================

export interface UseVisibleNodesProps {
  /** Todos os cards do nível superior (activeCards) */
  activeCards: CardData[];
  /** Mapa de grupos expandidos: subProcessId -> cards filhos */
  expandedGroups: Map<string, CardData[]>;
}

export function useVisibleNodes({
  activeCards,
  expandedGroups
}: UseVisibleNodesProps): UseVisibleNodesResult {
  
  // Calcular lista de nós visíveis e mapeamentos
  const result = useMemo(() => {
    const visibleNodes: CardData[] = [];
    const expandedGroupsInfo = new Map<string, ExpandedGroupInfo>();
    const entryRedirects = new Map<string, string>();
    const exitRedirects = new Map<string, string>();
    const childToGroup = new Map<string, string>();
    
    // Iterar sobre todos os cards do nível superior
    activeCards.forEach(card => {
      const isExpanded = expandedGroups.has(card.id);
      
      if (isExpanded && isGroupedSubProcess(card)) {
        // GRUPO EXPANDIDO: Substituir pelo seus filhos
        const childCards = expandedGroups.get(card.id) || [];
        
        if (childCards.length > 0) {
          // Ordenar filhos por posição
          const sortedChildren = sortCardsByPosition(childCards);
          const entryCard = findEntryCard(childCards);
          const exitCard = findExitCard(childCards);
          
          // Armazenar informações do grupo
          expandedGroupsInfo.set(card.id, {
            groupId: card.id,
            childCards: sortedChildren,
            entryCard,
            exitCard
          });
          
          // Configurar redirecionamentos
          entryRedirects.set(card.id, entryCard.id);
          exitRedirects.set(card.id, exitCard.id);
          
          // Mapear cada filho para o grupo pai
          childCards.forEach(child => {
            childToGroup.set(child.id, card.id);
          });
          
          // Adicionar os filhos à lista de nós visíveis
          sortedChildren.forEach(child => {
            visibleNodes.push(child);
          });
        } else {
          // Grupo expandido mas sem filhos? Mostrar o próprio grupo
          visibleNodes.push(card);
        }
      } else {
        // Card normal ou grupo minimizado: adicionar diretamente
        visibleNodes.push(card);
      }
    });
    
    const redirectMap: EdgeRedirectMap = {
      entryRedirects,
      exitRedirects,
      childToGroup
    };
    
    return { visibleNodes, expandedGroupsInfo, redirectMap };
  }, [activeCards, expandedGroups]);
  
  // Funções de consulta
  const isExpandedChild = useMemo(() => {
    return (cardId: string): boolean => {
      return result.redirectMap.childToGroup.has(cardId);
    };
  }, [result.redirectMap.childToGroup]);
  
  const getEntryNodeId = useMemo(() => {
    return (cardId: string): string => {
      return result.redirectMap.entryRedirects.get(cardId) || cardId;
    };
  }, [result.redirectMap.entryRedirects]);
  
  const getExitNodeId = useMemo(() => {
    return (cardId: string): string => {
      return result.redirectMap.exitRedirects.get(cardId) || cardId;
    };
  }, [result.redirectMap.exitRedirects]);
  
  return {
    visibleNodes: result.visibleNodes,
    expandedGroupsInfo: result.expandedGroupsInfo,
    redirectMap: result.redirectMap,
    isExpandedChild,
    getEntryNodeId,
    getExitNodeId
  };
}

// ============================================================================
// FUNÇÃO UTILITÁRIA PARA EDGE CALCULATIONS
// ============================================================================

/**
 * Processa conexões redirecionando para nós visíveis quando grupos estão expandidos
 * 
 * @param connections - Conexões originais do card
 * @param sourceCard - Card de origem
 * @param redirectMap - Mapa de redirecionamentos
 * @param expandedGroupsInfo - Informações sobre grupos expandidos
 * @returns Conexões processadas com redirecionamentos aplicados
 */
export function processConnectionsForExpandedGroups(
  sourceCardId: string,
  connections: CardData['conexoes_saida'],
  redirectMap: EdgeRedirectMap,
  expandedGroupsInfo: Map<string, ExpandedGroupInfo>
): Array<{ originalTargetId: string; effectiveTargetId: string; connection: typeof connections[0] }> {
  if (!connections || connections.length === 0) return [];
  
  return connections.map(conn => {
    // Verificar se o target é um grupo expandido
    const groupInfo = expandedGroupsInfo.get(conn.targetId);
    
    if (groupInfo) {
      // Target é um grupo expandido: redirecionar para o card de entrada
      return {
        originalTargetId: conn.targetId,
        effectiveTargetId: groupInfo.entryCard.id,
        connection: conn
      };
    }
    
    // Target não é grupo expandido: manter original
    return {
      originalTargetId: conn.targetId,
      effectiveTargetId: conn.targetId,
      connection: conn
    };
  });
}

/**
 * Verifica se um card de origem é filho de grupo expandido e deve redirecionar
 * suas conexões de saída para apontar para fora do grupo
 */
export function getEffectiveSourceId(
  cardId: string,
  redirectMap: EdgeRedirectMap,
  expandedGroupsInfo: Map<string, ExpandedGroupInfo>
): string {
  // Verificar se este card é filho de um grupo expandido
  const groupId = redirectMap.childToGroup.get(cardId);
  if (!groupId) return cardId;
  
  const groupInfo = expandedGroupsInfo.get(groupId);
  if (!groupInfo) return cardId;
  
  // Se este card é o último do grupo (exit), suas conexões vão para fora
  if (groupInfo.exitCard.id === cardId) {
    return cardId; // Manter o próprio card como source
  }
  
  return cardId;
}

export default useVisibleNodes;
